import React, {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Select } from 'antd';
import { ClockCircleOutlined, UnorderedListOutlined } from '@ant-design/icons';
import get from 'lodash/get';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';

import AppointmentCalendar from './AppointmentCalendar';
import { bookingTimeReducer } from './bookingTimeReducer';
import InlineAlert from '../../../../components/common/InlineAlert';
import KeyTerm from '../../../../components/common/KeyTerm';
import BookingActionBar from '../../../../components/NewBooking/BookingActionBar';
import TermsAccordion, {
  areAllSectionsAgreed,
  TermsSection,
} from '../../../../components/common/TermsAccordion';
import TimeslotGrid from '../../../../components/NewBooking/TimeslotGrid';
import {
  Card,
  CardBody,
  SectionTitle,
} from '../../../../components/common/Card';
import { fetchSpecialistsTimeSchedule } from '../../../../API/specialist';
import { updateBookingData } from '../../../../slices/booking';
import { APPOINTMENT_TYPE } from '../../../../constants';
import {
  BOOKING_TERMS_NOT_AGREED_MESSAGE,
  BOOKING_TERMS_SECTIONS,
  TERMS_VERSION,
} from '../../../../constants/terms';
import { TIME_FORMAT } from '../../../../constants/time';
import { AUS_TIMEZONE_LIST_ONLY_LABEL } from '../../../../constants/timezone';
import { generateAppointmentType } from '../../../../utils/bookingUtils';
import { formatGapFeeDisplay } from '../../../../utils/feeUtils';
import {
  getCurrentMonth,
  getTimezoneFromAddress,
} from '../../../../utils/timezoneUtils';
import { scrollToElement } from '../../../../utils/scrollUtils';
import { buildTimeslotOptions } from '../../../../utils/timeslotUtils';
import {
  Field,
  FieldHint,
  FieldLabel,
  RequiredMark,
  TimezoneSelect,
} from './styles';

const { MONTH, DATE_AND_TIME, DATE_WITH_DASH } = TIME_FORMAT;

const TERMS_GROUP_TITLE = 'Terms \u2014 please open, read and tick each one';
const GAP_FEE_LABEL = 'Out of Pocket (Gap Fee) \u00b7 estimate';
const SELECT_DATE_MESSAGE = 'Please select appointment date.';
const SELECT_TIME_MESSAGE = 'Please select appointment time.';
const NO_DATE_SELECTED_MESSAGE =
  'Select your preferred appointment time \u2014 pick a date above first.';
const NO_TIMESLOT_MESSAGE =
  'There are no appointment times left on this date. Please choose another date.';

const BookTimePicker = () => {
  const reduxDispatch = useDispatch();
  const workflow = useSelector(({ workflow }) => workflow.data);
  const { data: bookingData } = useSelector(({ booking }) => booking);
  const appointmentTime = bookingData?.appointmentTime;

  const appointmentType = generateAppointmentType(
    get(workflow, 'appointment.isNewAppointment', false)
  );
  const appointmentLength =
    workflow?.specialist?.appointmentLength?.[`${appointmentType}Time`];
  const dueDate = get(workflow, 'appointment.dueDate', '');
  const addressResidential = get(workflow, 'client.addressResidential', null);

  const [timezone, setTimezone] = useState(
    () =>
      appointmentTime?.timezone || getTimezoneFromAddress(addressResidential)
  );

  // The calendar opens on this month in the booking's timezone, or on a
  // review appointment's later due month.
  const [bookingTime, dispatch] = useReducer(bookingTimeReducer, {
    date: null,
    month:
      appointmentType === APPOINTMENT_TYPE.REVIEW &&
      moment().isBefore(moment(dueDate))
        ? moment(dueDate).format(MONTH)
        : getCurrentMonth(timezone),
    time: null,
  });
  const [availableTimeList, setAvailableTimeList] = useState([]);
  const [scheduleError, setScheduleError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [agreements, setAgreements] = useState(
    () => bookingData?.bookingTerms?.agreements || {}
  );
  const [showTermsError, setShowTermsError] = useState(false);

  const termsRef = useRef(null);
  const dateTimeRef = useRef(null);

  const hasAgreedToAllTerms = areAllSectionsAgreed(
    BOOKING_TERMS_SECTIONS,
    agreements
  );

  useEffect(() => {
    if (!appointmentTime?.start || !appointmentTime?.end) return;

    const { start, end, timezone: storedTimezone } = appointmentTime;
    const startMoment = storedTimezone
      ? momentTimezone(start).tz(storedTimezone)
      : momentTimezone.parseZone(start);

    dispatch({
      type: 'preloadData',
      data: {
        date: startMoment.format(DATE_WITH_DASH),
        month: startMoment.format(MONTH),
        time: { start, end },
      },
    });
  }, [appointmentTime]);

  useEffect(() => {
    (async () => {
      if (
        !appointmentLength ||
        !timezone ||
        !bookingTime.month ||
        !appointmentType
      ) {
        return;
      }

      const { data, error } = await fetchSpecialistsTimeSchedule({
        specialistId: get(workflow, ['specialist', 'id'], ''),
        month: bookingTime.month,
        timezone,
        newOrReview: appointmentType,
      });

      if (data && !error) {
        setAvailableTimeList(data);
        setScheduleError('');
        return;
      }

      if (error) {
        setScheduleError(
          `Something went wrong when fetching available time: ${error}`
        );
      }
    })();
  }, [
    timezone,
    workflow,
    appointmentLength,
    appointmentType,
    bookingTime.month,
  ]);

  const timeslots = useMemo(
    () =>
      buildTimeslotOptions({
        bookingDate: bookingTime.date,
        appointmentLength,
        availableTimeList,
        timezone,
      }),
    [bookingTime.date, appointmentLength, availableTimeList, timezone]
  );

  const selectedTimeslot = bookingTime.time?.start
    ? momentTimezone(bookingTime.time.start).tz(timezone).format(DATE_AND_TIME)
    : '';

  const handleSelectTimeslot = (timeslot) => {
    const startMoment = momentTimezone.tz(timeslot, DATE_AND_TIME, timezone);

    dispatch({
      type: 'updateTime',
      time: {
        start: startMoment.format(),
        end: startMoment.clone().add(appointmentLength, 'minutes').format(),
      },
    });
    setErrorMessage('');
  };

  const handleUpdateTimezone = useCallback((selectedTimezone) => {
    dispatch({ type: 'updateTime', time: null });
    setTimezone(selectedTimezone);
  }, []);

  const handleAgreementChange = (sectionKey, isAgreed) => {
    setAgreements((current) => ({ ...current, [sectionKey]: isAgreed }));
    setShowTermsError(false);
  };

  const handleSubmit = () => {
    const hasSelectedDate = Boolean(bookingTime.date);
    const hasSelectedTime = Boolean(
      bookingTime.time?.start && bookingTime.time?.end
    );

    setShowTermsError(!hasAgreedToAllTerms);

    if (!hasSelectedDate) {
      setErrorMessage(SELECT_DATE_MESSAGE);
    } else if (!hasSelectedTime) {
      setErrorMessage(SELECT_TIME_MESSAGE);
    } else {
      setErrorMessage('');
    }

    if (!hasAgreedToAllTerms) {
      scrollToElement(termsRef.current);
      return false;
    }

    if (!hasSelectedDate || !hasSelectedTime) {
      scrollToElement(dateTimeRef.current);
      return false;
    }

    reduxDispatch(
      updateBookingData({
        appointmentTime: {
          timezone,
          ...bookingTime.time,
        },
        bookingTerms: {
          version: TERMS_VERSION,
          agreements,
        },
      })
    );

    return true;
  };

  const appointmentLengthLabel = appointmentLength
    ? ` (${appointmentLength} minutes)`
    : '';
  const gapFeeDisplay = formatGapFeeDisplay(
    get(workflow, 'specialist.fee', null)
  );

  return (
    <>
      <Card>
        <CardBody>
          <SectionTitle>What you&rsquo;re agreeing to</SectionTitle>
          <KeyTerm icon={<ClockCircleOutlined />}>
            <b>Your payment link arrives by SMS after you book.</b> Pay it to
            secure your appointment &mdash; if we haven&rsquo;t received payment{' '}
            <b>3 business days</b> before, we&rsquo;ll send you a reminder.
          </KeyTerm>
          <KeyTerm icon={<UnorderedListOutlined />}>
            Missed appointments, or appointments cancelled without valid medical
            reason within <b>2 business days</b> of the appointment will incur a{' '}
            <b>fee ($150)</b>.
          </KeyTerm>
        </CardBody>
      </Card>

      <TermsSection ref={termsRef}>
        <TermsAccordion
          groupTitle={TERMS_GROUP_TITLE}
          sections={BOOKING_TERMS_SECTIONS}
          agreements={agreements}
          onAgreementChange={handleAgreementChange}
          errorMessage={BOOKING_TERMS_NOT_AGREED_MESSAGE}
          showError={showTermsError}
        />
      </TermsSection>

      <Card>
        <CardBody>
          <SectionTitle>Select your preferred appointment date</SectionTitle>
          <AppointmentCalendar
            availableTimeList={availableTimeList}
            bookingTime={bookingTime}
            timezone={timezone}
            dispatch={dispatch}
          />
        </CardBody>
      </Card>

      <Card ref={dateTimeRef}>
        <CardBody>
          <SectionTitle>Please select a Date / Time</SectionTitle>

          <Field>
            <FieldLabel htmlFor="appointment-timezone">
              <RequiredMark>*</RequiredMark>Timezone
            </FieldLabel>
            <FieldHint>
              Set from your postcode &mdash; change it if you&rsquo;ll be
              somewhere else at your appointment time.
            </FieldHint>
            <TimezoneSelect
              id="appointment-timezone"
              placeholder="Select your time zone"
              value={timezone}
              onSelect={handleUpdateTimezone}
            >
              {AUS_TIMEZONE_LIST_ONLY_LABEL.map((zone) => (
                <Select.Option value={zone} key={zone}>
                  {zone}
                </Select.Option>
              ))}
            </TimezoneSelect>
          </Field>

          <Field>
            <FieldLabel>
              <RequiredMark>*</RequiredMark>
              {`Appointment Time${appointmentLengthLabel}`}
            </FieldLabel>
            <TimeslotGrid
              key={bookingTime.date}
              timeslots={timeslots}
              selectedTimeslot={selectedTimeslot}
              onSelect={handleSelectTimeslot}
              emptyMessage={
                bookingTime.date
                  ? NO_TIMESLOT_MESSAGE
                  : NO_DATE_SELECTED_MESSAGE
              }
            />
          </Field>

          {errorMessage && <InlineAlert>{errorMessage}</InlineAlert>}
          {scheduleError && <InlineAlert>{scheduleError}</InlineAlert>}
        </CardBody>
      </Card>

      <BookingActionBar
        amountLabel={GAP_FEE_LABEL}
        amount={gapFeeDisplay}
        onNext={handleSubmit}
      />
    </>
  );
};

export default BookTimePicker;
